import { useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { BillingPanelBloc } from "@/application/billing/billing_bloc";
import { billingRepository } from "@/application/billing/billing_repository";
import type {
  BillingAccountRow,
  BillingEventRow,
} from "@/application/billing/types";
import { Button } from "@/components/ui/base/button";
import { ProviderPill } from "@/components/ui/ProviderPill";
import { toPillProvider } from "@/components/ui/provider-pill-providers";
import { StageBadge } from "./StageBadge";
import { subscriptionUrl } from "@/lib/stripe-links";

/**
 * What each provider means here, beyond its brand name.
 *
 * `cpos` is the one that needs explaining: it is not a payment rail but us
 * granting a plan directly, which is the normal case for every merchant on the
 * free tier. Without the note it reads like a fourth store.
 */
const PROVIDER_NOTES: Record<string, string> = {
  apple: "In-app purchase",
  google: "In-app purchase",
  cpos: "No payment rail — plan granted directly",
};

function providerNote(provider: string | null): string | null {
  if (!provider) return null;
  // eslint-disable-next-line security/detect-object-injection -- lookup keyed by a persisted provider string, missing keys fall through
  return PROVIDER_NOTES[provider] ?? null;
}

/**
 * A provider as a brand pill, falling back to its raw persisted value so a
 * provider we have no mark for still reads as data rather than as a gap.
 */
function ProviderLabel({
  provider,
  iconOnly,
}: {
  provider: string | null;
  iconOnly?: boolean;
}) {
  const pill = toPillProvider(provider);
  if (pill) return <ProviderPill provider={pill} iconOnly={iconOnly} />;
  return <span className="text-foreground">{provider ?? "—"}</span>;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function Field({
  label,
  value,
  mono,
  href,
}: {
  label: string;
  value: string;
  mono?: boolean;
  href?: string;
}) {
  // Keep the colour out of the shared type classes: Tailwind resolves
  // competing text-* colours by stylesheet order, not by the order they appear
  // in this string, so a `text-foreground` here can silently win over the link
  // colour appended after it.
  const typeClassName = mono
    ? // Vendor ids are long and unbreakable; without this they run past
      // the card edge rather than wrapping inside it.
      "font-mono text-xs break-all"
    : "text-sm";

  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${typeClassName} block text-blue-600 underline hover:text-blue-800`}
        >
          {value}
        </a>
      ) : (
        <p className={`${typeClassName} text-foreground`}>{value}</p>
      )}
    </div>
  );
}

/**
 * How to label `periodEnd`, which carries two different facts in one column.
 *
 * `autoRenew` is the only thing separating "renews on the 11th" from "ends on
 * the 11th". Unknown keeps the neutral label rather than guessing: a granted
 * plan has no renewal cycle at all, and reading that as either one would be a
 * claim the projection never made.
 */
function periodLabel(autoRenew: boolean | null): string {
  if (autoRenew === null) return "Period end";
  return autoRenew ? "Renews" : "Access ends";
}

function CurrentState({ account }: { account: BillingAccountRow }) {
  const note = providerNote(account.provider);

  return (
    <div className="bg-card rounded-lg border p-4">
      <h3 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide uppercase">
        Current state
      </h3>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {account.state && <StageBadge stage={account.state} />}
        {account.plan && (
          <span className="bg-muted text-foreground inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">
            {account.plan}
          </span>
        )}
        <span
          className={[
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
            account.active
              ? "bg-green-100 text-green-700"
              : "bg-muted text-muted-foreground",
          ].join(" ")}
        >
          {account.active ? "entitled" : "not entitled"}
        </span>
        {/* Only an explicit `false` earns the badge. A merchant whose flag is
            unknown is not cancelling, and this badge is the first thing an admin
            reads when a merchant asks why they were charged. */}
        {account.autoRenew === false && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
            not renewing
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <p className="text-muted-foreground text-xs">Provider</p>
          <div className="mt-1 text-sm">
            <ProviderLabel provider={account.provider} />
          </div>
          {note && (
            <p className="text-subtle-foreground mt-1 text-xs">{note}</p>
          )}
        </div>
        <Field
          label={periodLabel(account.autoRenew)}
          value={formatDate(account.periodEnd)}
        />
        {account.canceledAt && (
          <Field label="Cancelled" value={formatDate(account.canceledAt)} />
        )}
        <Field
          label="Trial consumed"
          value={formatDate(account.trialConsumedAt)}
        />
        <Field label="Updated" value={formatDate(account.updatedAt)} />
        {account.stripeSubscriptionId && (
          <Field
            label="Stripe subscription"
            value={account.stripeSubscriptionId}
            href={subscriptionUrl(account.stripeSubscriptionId)}
            mono
          />
        )}
        {account.iapAccountToken && (
          <Field
            label="Store account token"
            value={account.iapAccountToken}
            mono
          />
        )}
        <Field
          label="Version"
          value={account.version === null ? "—" : String(account.version)}
        />
      </div>
    </div>
  );
}

/** The entitlement consequence of one transition, when it had one. */
function entitlementNote(event: BillingEventRow): string | null {
  const { previousActive, newActive } = event;
  if (newActive === null || previousActive === newActive) return null;
  return newActive ? "gained entitlement" : "lost entitlement";
}

/**
 * The renewal consequence of one transition, when it had one.
 *
 * Carries the whole meaning of a `renewal_canceled` / `renewal_resumed` row: no
 * state moves on either, so without this the two are distinguishable only by the
 * action name.
 */
function renewalNote(event: BillingEventRow): string | null {
  const { previousAutoRenew, newAutoRenew } = event;
  if (newAutoRenew === null || previousAutoRenew === newAutoRenew) return null;
  return newAutoRenew ? "resumed renewing" : "stopped renewing";
}

/**
 * The tier movement, when there was one.
 *
 * The upgrade an admin is most often asked about, and the one the state columns
 * cannot show: `free -> pro` while the account stays `active` is a real
 * transition whose every other visible column is unchanged.
 */
function planNote(event: BillingEventRow): string | null {
  const { previousPlan, newPlan } = event;
  if (!newPlan || previousPlan === newPlan) return null;
  return previousPlan ? `${previousPlan} → ${newPlan}` : newPlan;
}

/**
 * The state pair, when the state actually moved.
 *
 * Rendered unconditionally this read `active → active` on every plan change and
 * every renewal — the most prominent text on the row saying that nothing
 * happened, next to notes saying what did. A pair that has not moved is noise
 * that competes with the signal beside it.
 */
function stateNote(event: BillingEventRow): string | null {
  const { previousState, newState } = event;
  if (!newState) return null;
  if (!previousState) return newState;
  return previousState === newState ? null : `${previousState} → ${newState}`;
}

function TimelineEntry({ event }: { event: BillingEventRow }) {
  const entitlement = entitlementNote(event);
  const renewal = renewalNote(event);
  const plan = planNote(event);
  const state = stateNote(event);

  return (
    <li className="relative pb-5 pl-6 last:pb-0">
      <span className="bg-muted-foreground/50 absolute top-1.5 left-0 h-2 w-2 rounded-full" />
      <span className="bg-muted absolute top-3.5 bottom-0 left-[3px] w-px last:hidden" />

      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-foreground text-sm font-medium">
          {event.action.replace(/_/g, " ")}
        </span>
        {state && (
          <span className="text-muted-foreground text-sm">{state}</span>
        )}
        {plan && (
          <span className="bg-muted text-foreground inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold">
            {plan}
          </span>
        )}
        <span className="text-subtle-foreground ml-auto text-xs whitespace-nowrap">
          {formatDate(event.occurredAt)}
        </span>
      </div>

      <div className="text-subtle-foreground mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
        {entitlement && (
          <span
            className={
              event.newActive
                ? "font-medium text-green-600"
                : "text-muted-foreground font-medium"
            }
          >
            {entitlement}
          </span>
        )}
        {renewal && (
          <span
            className={
              event.newAutoRenew
                ? "font-medium text-green-600"
                : "font-medium text-amber-600"
            }
          >
            {renewal}
          </span>
        )}
        {event.provider && <ProviderLabel provider={event.provider} iconOnly />}
        {event.effectiveAt && (
          <span>effective {formatDate(event.effectiveAt)}</span>
        )}
        {event.sourceEventId && (
          <span className="font-mono break-all">{event.sourceEventId}</span>
        )}
      </div>
    </li>
  );
}

/**
 * Remounts the panel per merchant rather than handing it a new bloc.
 *
 * The merchant route swaps `businessId` without remounting, and
 * `useStateMachineValue` seeds its React state on mount and only re-syncs in an
 * effect — so swapping the bloc renders one frame of the previous merchant's
 * billing under the new merchant's name. Brief, but this is a tool people use to
 * compare merchants, and a wrong plan read confidently is worse than a spinner.
 *
 * The `key` makes React unmount and rebuild, so the first frame is always this
 * merchant's own loading state.
 */
export function BillingTab({ businessId }: { businessId: string }) {
  return <BillingPanel key={businessId} businessId={businessId} />;
}

function BillingPanel({ businessId }: { businessId: string }) {
  const bloc = useMemo(
    () => new BillingPanelBloc(billingRepository, businessId),
    [businessId],
  );
  const [state] = useStateMachineValue(bloc);

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  if (state.status === "loading" || state.status === "idle") {
    return <p className="text-muted-foreground text-sm">Loading billing…</p>;
  }

  if (state.status === "error") {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {state.error}
      </div>
    );
  }

  return (
    // Stacked rather than a fixed two-column split: the summary's field grid and
    // the timeline's transition rows both need horizontal room, and a sidebar
    // column crushes long vendor ids at anything below a wide viewport.
    <div className="space-y-6">
      {state.account ? (
        <CurrentState account={state.account} />
      ) : (
        <div className="bg-card rounded-lg border p-4">
          <h3 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
            Current state
          </h3>
          <p className="text-muted-foreground text-sm">
            No billing projection for this merchant yet. Rows are written by the
            Stripe event path and by the nightly reconciler sweep, so a merchant
            who signed up since the last sweep has none.
          </p>
        </div>
      )}

      <div className="bg-card rounded-lg border p-4">
        <h3 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide uppercase">
          History
        </h3>

        {state.events.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No recorded changes. The log is appended whenever the projection
            moves, so a merchant whose billing has never changed has no entries.
          </p>
        ) : (
          <ul>
            {state.events.map((event) => (
              <TimelineEntry key={event.id} event={event} />
            ))}
          </ul>
        )}

        {state.error && state.events.length > 0 && (
          <p className="mt-3 text-xs text-red-600">{state.error}</p>
        )}

        {state.nextToken && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              disabled={state.loadingMore}
              onClick={() => void bloc.loadMore()}
            >
              {state.loadingMore && (
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              )}
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
